// src/components/onboarding/steps/Step6Complete.tsx
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clipboard, ExternalLink } from 'lucide-react';
import { OnboardingData } from '@/types/onboarding';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { useBranding } from '@/contexts/BrandingContext';
// import { usePostHog } from 'posthog-js/react'

interface Step6CompleteProps {
  data: OnboardingData;
  navigateToDashboard: () => void;
}

// Helper function to get display name for calendar provider
const getProviderDisplayName = (providerKey: string | null): string => {
    if (!providerKey) return 'Calendar'; // Default if null
    const names: Record<string, string> = {
        google: 'Google Calendar',
        outlook: 'Outlook Calendar', 
        acuity: 'Acuity Scheduling',
        calendly: 'Calendly',
        square: 'Square Appointments',
    };
    return names[providerKey.toLowerCase()] || (providerKey.charAt(0).toUpperCase() + providerKey.slice(1) + ' Calendar');
};


const Step6Complete: React.FC<Step6CompleteProps> = ({ data, navigateToDashboard }) => {
    const { branding } = useBranding();
    // const posthog = usePostHog()

    // React.useEffect(() => {
    //     posthog.capture('onboarding_complete', {
    //         userId: data.userId, // Assuming userId is part of OnboardingData or accessible
    //         enabledFunctions: Object.keys(data.callActivationSettings).filter(k => data.callActivationSettings[k].enabled),
    //         calendarConnected: !!data.calendarProvider
    //     })
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []) // Ensure dependencies are correct if userId or other data might change

    const copyToClipboard = () => {
        if (data.assignedPhoneNumber && data.assignedPhoneNumber !== "Number provisioning pending...") {
            navigator.clipboard.writeText(data.assignedPhoneNumber)
                .then(() => toast.success("Phone number copied to clipboard!"))
                .catch(() => toast.error("Failed to copy number."));
        } else {
            toast.info("Phone number is not available yet.");
        }
    };

    const enabledFunctions = Object.entries(data.callActivationSettings)
        .filter(([, value]) => value.enabled)
        .map(([key]) => {
            switch (key) {
                case 'confirm_appointments': return 'Confirming Appointments';
                case 'recover_cancellations': return 'Recovering Cancellations';
                case 'reschedule_noshows': return 'Rescheduling No-Shows';
                default: return key;
            }
        });


  return (
    <div className="text-center">
        <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
      <h1 className="text-3xl font-bold mb-3 text-gray-800 dark:text-gray-100">
        {branding?.customCopy?.successTitle || 'Success! Your AI Employee is Ready!'}
      </h1>
       <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
        <strong>{data.aiName || branding?.customCopy?.aiName || 'Your Assistant'}</strong> {branding?.customCopy?.successMessage || 'is now active'}
        {data.calendarProvider && data.calendarEmail ? (
          <> and connected to your <strong>{getProviderDisplayName(data.calendarProvider)}</strong>.</>
        ) : (
          <>
            . You can connect a calendar later from your dashboard to enable
            full appointment management features.
          </>
        )}
      </p>

       {/* Key Information Card */}
       <Card className="mb-8 text-left bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
         <CardHeader>
            <CardTitle className="text-xl">Key Information</CardTitle>
            <CardDescription>Your AI is configured as follows:</CardDescription>
         </CardHeader>
         <CardContent className="space-y-4">
             {/* Assigned Phone Number */}
             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                 <Label className="font-semibold text-gray-700 dark:text-gray-300 mb-1 sm:mb-0">Your AI's Phone Number:</Label>
                 {data.assignedPhoneNumber && data.assignedPhoneNumber !== "Number provisioning pending..." ? (
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-lg font-mono px-3 py-1">{data.assignedPhoneNumber}</Badge>
                        <Button variant="ghost" size="sm" onClick={copyToClipboard} aria-label="Copy phone number">
                            <Clipboard size={16} />
                        </Button>
                    </div>
                 ) : (
                    <Badge variant="secondary" className="text-sm px-3 py-1 animate-pulse">
                       {data.assignedPhoneNumber || "Provisioning..."}
                    </Badge>
                 )}
             </div>

              {/* Enabled Functions */}
              <div>
                  <Label className="font-semibold text-gray-700 dark:text-gray-300 block mb-1">Initial AI Tasks:</Label>
                  {enabledFunctions.length > 0 ? (
                     <div className="flex flex-wrap gap-2">
                         {enabledFunctions.map((func, index) => (
                             <Badge key={index} variant="default">{func}</Badge>
                         ))}
                     </div>
                  ) : (
                     <p className="text-sm text-gray-500 dark:text-gray-400">No specific outbound tasks enabled initially.</p>
                  )}
              </div>

         </CardContent>
       </Card>

       {/* Next Steps Guidance */}
       <div className="text-left space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-8">
           <p><strong>Next Steps:</strong></p>
           <ul className="list-disc list-inside space-y-1">
                <li>Your AI will begin working based on your settings and the AI calling hours you defined.</li>
                {(!data.calendarProvider || !data.calendarEmail) && <li>Consider connecting your calendar via the dashboard to enable appointment booking and management.</li>}
                <li>Explore your dashboard to customize further, manage specific call flows, add detailed business info (like specific service details or cancellation policies), and view call reports.</li>
           </ul>
       </div>


      <Button onClick={navigateToDashboard} size="lg">
        {branding?.customCopy?.dashboardButtonText || 'Go to Dashboard'}
        <ExternalLink size={16} className="ml-2" />
      </Button>
    </div>
  );
};

export default Step6Complete;